import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  Filter,
  Eye,
  MapPin,
  Calendar,
  ArrowUpDown,
  X,
  Play,
  Newspaper,
  ClipboardList,
} from "lucide-react";
import { HeaderPage } from "@/components/HeaderPage";
import { CardContent } from "@/components/CardContent";
import { Table } from "@/components/Table";
import {
  useGetCategories,
  useGetOcurrences,
  useGetSubCategoriesByCategoryId,
} from "@/hooks/useOccurrences";
import type { OccurrenceDto } from "@/dtos/CategoriesResponseDto";
import { formatDate } from "@/lib/utils";
import { useState, useEffect } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import { useNavigate } from "react-router-dom";
import type { Category } from "@/types/Category";
import type { SubCategory } from "@/types/SubCategory";
import { Loading } from "@/components/Loading";
import { useChatNotifications } from "@/context/ChatNotificationsContext";
import { MessageSquare } from "lucide-react";

const statusConfig: Record<
  string,
  {
    label: string;
    variant:
      | "default"
      | "secondary"
      | "destructive"
      | "outline"
      | "warning"
      | "alert";
  }
> = {
  AWAITING_ANALYSIS: { label: "Aguardando Análise", variant: "outline" },
  IN_PROGRESS: { label: "Em Andamento", variant: "secondary" },
  RESOLVED: { label: "Resolvida", variant: "default" },
  // REJECTED: { label: "Rejeitada", variant: "destructive" },
  CANCELLED: { label: "Cancelada", variant: "destructive" },
  REOPENED: { label: "Reaberta", variant: "outline" },
  RETURNED: { label: "Devolvida", variant: "warning" },
  AWAITING_REANALYSIS: { label: "Aguardando Reanálise", variant: "secondary" },
  FILED: { label: "Arquivada", variant: "alert" },
};

const statusOptions = [
  { value: "all", label: "Todos" },
  { value: "AWAITING_ANALYSIS", label: "Aguardando Analise" },
  { value: "IN_PROGRESS", label: "Em Andamento" },
  { value: "RESOLVED", label: "Resolvida" },
  // { value: "REJECTED", label: "Rejeitada" },
  { value: "CANCELLED", label: "Cancelada" },
  { value: "REOPENED", label: "Reaberta" },
  { value: "RETURNED", label: "Devolvida" },
  { value: "AWAITING_REANALYSIS", label: "Aguardando Reanalise" },
  { value: "FILED", label: "Arquivada" },
];

const priorityConfig: Record<string, { label: string; color: string }> = {
  LOW: {
    label: "Baixa",
    color: "bg-green-100 text-green-700",
  },
  MEDIUM: { label: "Média", color: "bg-yellow-100 text-yellow-700" },
  HIGH: { label: "Alta", color: "bg-orange-100 text-orange-700" },
  URGENT: {
    label: "Crítica",
    color: "bg-red-600 text-white",
  },
};

const calculateSLA = (
  slaDeadline: string | null | undefined,
  createdAt: string,
  status: string,
): {
  display: string;
  color: string;
  isOverdue: boolean;
} => {
  if (!slaDeadline) {
    return {
      display: "N/A",
      color: "bg-gray-100 text-gray-700",
      isOverdue: false,
    };
  }

  const now = new Date();
  const deadline = new Date(slaDeadline);
  const created = new Date(createdAt);

  // Calcular tempo total do SLA (da criação até o deadline)
  const totalSlaMs = deadline.getTime() - created.getTime();

  // Calcular tempo restante (ou passou)
  const remainingMs = deadline.getTime() - now.getTime();
  const isOverdue = remainingMs < 0;

  // Calcular horas e dias
  const absMs = Math.abs(remainingMs);
  const totalHours = absMs / (1000 * 60 * 60);
  const days = Math.floor(absMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor(absMs / (1000 * 60 * 60));

  // Formato de exibição: se mais de 24h, mostra em dias, senão em horas
  let display: string;
  if (totalHours >= 24) {
    display = `${isOverdue ? "-" : ""}${days} dia${days !== 1 ? "s" : ""}`;
  } else {
    display = `${isOverdue ? "-" : ""}${hours}h`;
  }

  // Se já foi resolvido, mostrar como verde (concluído no prazo)
  if (status === "RESOLVED" || status === "COMPLETED") {
    return {
      display,
      color: "bg-green-100 text-green-700 border-green-300",
      isOverdue: false,
    };
  }

  // Se passou do prazo, vermelho
  if (isOverdue) {
    return {
      display,
      color: "bg-red-100 text-red-700 border-red-300",
      isOverdue: true,
    };
  }

  // Calcular percentual restante
  const percentRemaining = (remainingMs / totalSlaMs) * 100;

  // Definir cor baseado no percentual
  let color: string;
  if (percentRemaining > 50) {
    color = "bg-green-100 text-green-700 border-green-300";
  } else if (percentRemaining >= 25) {
    color = "bg-yellow-100 text-yellow-700 border-yellow-300";
  } else {
    color = "bg-red-100 text-red-700 border-red-300";
  }

  return {
    display,
    color,
    isOverdue,
  };
};

export const Occurrences = () => {
  const navigate = useNavigate();
  const { unreadCounts } = useChatNotifications();
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [cpfInput, setCpfInput] = useState("");
  const [userNameInput, setUserNameInput] = useState("");
  const [cpf, setCpf] = useState("");
  const [userName, setUserName] = useState("");
  const [onlyReopened, setOnlyReopened] = useState(false);
  const [onlyAnonymous, setOnlyAnonymous] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCpf(cpfInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [cpfInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setUserName(userNameInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [userNameInput]);

  const { data, isLoading } = useGetOcurrences({
    page: currentPage,
    limit: 10,
    status: status || undefined,
    priority: priority && priority !== "all" ? priority : undefined,
    categoryId: categoryId || undefined,
    subcategoryId: subcategoryId || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    sortBy: sortBy || undefined,
    sortOrder,
    cpf: cpf || undefined,
    userName: userName || undefined,
    onlyReopened: onlyReopened || undefined,
    search: debouncedSearch || undefined,
    isAnonymous: onlyAnonymous || undefined,
  });
  const { data: categoriesData } = useGetCategories();
  const { data: subCategoriesData } = useGetSubCategoriesByCategoryId(
    categoryId || "",
  );
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const clearFilters = () => {
    setSearch("");
    setStatus("");
    setPriority("");
    setCategoryId("");
    setSubcategoryId("");
    setStartDate("");
    setEndDate("");
    setSortBy("");
    setSortOrder("desc");
    setCpfInput("");
    setUserNameInput("");
    setCpf("");
    setUserName("");
    setOnlyReopened(false);
    setOnlyAnonymous(false);
    setCurrentPage(1);
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  return (
    <>
      <HeaderPage
        title="Ocorrências"
        description="Gerencie as ocorrências registradas na plataforma."
      />
      <Card className="mb-6 mt-6 bg-white">
        <CardContentUI className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Busca textual em protocolo, descrição e endereço."
                  className="pl-10 h-10 text-sm focus:ring-emerald-500 focus:border-transparent transition-all"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="flex flex-wrap gap-3">
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="w-[180px] h-10 text-sm">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        textValue={option.label}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger className="w-[180px] h-10 text-sm">
                    <SelectValue placeholder="Prioridade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="LOW">Baixa</SelectItem>
                    <SelectItem value="MEDIUM">Média</SelectItem>
                    <SelectItem value="HIGH">Alta</SelectItem>
                    <SelectItem value="URGENT">Crítica</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={categoryId}
                  onValueChange={(value) => {
                    setCategoryId(value);
                  }}
                >
                  <SelectTrigger className="w-[180px] h-10 text-sm">
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {categoriesData?.data.map((category: Category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 cursor-pointer hover:bg-gray-100 transition-colors ease-in-out duration-200"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter className="h-4 w-4" />
                </Button>
                {/* <Button
                  variant="outline"
                  className="h-12 text-base px-4 cursor-pointer hover:bg-gray-100 transition-colors ease-in-out duration-200"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Exportar
                </Button> */}
              </div>
            </div>

            {showFilters && (
              <div className="border-t pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">
                      Subcategoria
                    </label>
                    <Select
                      value={subcategoryId}
                      disabled={
                        !subCategoriesData || subCategoriesData.length === 0
                      }
                      onValueChange={setSubcategoryId}
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {subCategoriesData?.map((subcat: SubCategory) => (
                          <SelectItem key={subcat.id} value={subcat.id}>
                            {subcat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1.5 block">
                      Data Inicial
                    </label>
                    <Input
                      type="date"
                      className="h-10"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1.5 block">
                      Data Final
                    </label>
                    <Input
                      type="date"
                      className="h-10"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1.5 block">
                      Ordenar por
                    </label>
                    <div className="flex gap-2">
                      <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger className="h-10">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="createdAt">
                            Data de Criação
                          </SelectItem>
                          <SelectItem value="updatedAt">
                            Data de Atualização
                          </SelectItem>
                          <SelectItem value="status">Status</SelectItem>
                          <SelectItem value="protocol">Protocolo</SelectItem>
                          <SelectItem value="waitingTime">
                            Tempo de Espera
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 cursor-pointer hover:bg-gray-100 transition-colors ease-in-out duration-200"
                        onClick={toggleSortOrder}
                        title={
                          sortOrder === "asc" ? "Crescente" : "Decrescente"
                        }
                      >
                        <ArrowUpDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1.5 block">
                      CPF
                    </label>
                    <Input
                      placeholder="000.000.000-00"
                      className="h-10"
                      value={cpfInput}
                      onChange={(e) => setCpfInput(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1.5 block">
                      Nome do Usuário
                    </label>
                    <Input
                      placeholder="Digite o nome"
                      className="h-10"
                      value={userNameInput}
                      onChange={(e) => setUserNameInput(e.target.value)}
                    />
                  </div>

                  <div className="flex items-end">
                    <label className="flex items-center gap-2 h-10 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300"
                        checked={onlyReopened}
                        onChange={(e) => setOnlyReopened(e.target.checked)}
                      />
                      <span className="text-sm font-medium">
                        Apenas Reabertas
                      </span>
                    </label>
                  </div>

                  <div className="flex items-end">
                    <Button
                      variant="outline"
                      className="h-10 w-full cursor-pointer hover:bg-gray-100 transition-colors ease-in-out duration-200"
                      onClick={clearFilters}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Limpar Filtros
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContentUI>
      </Card>

      <CardContent
        title="Lista de Ocorrências"
        content={
          isLoading ? (
            <Loading label="Carregando ocorrências..." />
          ) : (
            <Table<OccurrenceDto>
              columns={[
                { key: "protocol", label: "Protocolo", render: (item) => (
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-sm">{item.protocol}</span>
                    {(unreadCounts[item.id] ?? 0) > 0 && (
                      <span
                        className="inline-flex items-center gap-0.5 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none"
                        title={`${unreadCounts[item.id]} mensagem(ns) não lida(s)`}
                      >
                        <MessageSquare className="h-2.5 w-2.5" />
                        {unreadCounts[item.id]}
                      </span>
                    )}
                  </div>
                ) },
                {
                  key: "category",
                  label: "Categoria",
                  render: (item) => (
                    <div>
                      <p className="font-medium text-sm">
                        {item.category.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {item.subcategory.name}
                      </p>
                    </div>
                  ),
                },
                {
                  key: "description",
                  label: "Descrição",
                  render: (item) => (
                    <p className="truncate max-w-[250px] text-sm text-gray-600">
                      {item.description}
                    </p>
                  ),
                },
                {
                  key: "neighborhood",
                  label: "Local",
                  render: (item) => (
                    <div className="flex items-start gap-1">
                      <MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500 truncate max-w-[150px]">
                          {item.address}
                        </p>
                      </div>
                    </div>
                  ),
                },
                {
                  key: "citizen",
                  label: "Cidadão",
                  render: (item) => (
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-gray-600">
                        {item.isAnonymous
                          ? "Usuário Anônimo"
                          : item.user.profile.fullName}
                      </span>
                      {item.isAnonymous && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-purple-300 text-purple-700 bg-purple-50">
                          Anônima
                        </Badge>
                      )}
                    </div>
                  ),
                },
                {
                  key: "priority",
                  label: "Prioridade",
                  render: (item) => {
                    const label =
                      item.status === "IN_PROGRESS"
                        ? item.priority
                        : item.userPerceivedUrgency;
                    const config = priorityConfig[
                      item.status === "IN_PROGRESS"
                        ? item.priority
                        : item.userPerceivedUrgency
                    ] || {
                      label: label,
                      color: "bg-gray-100 text-gray-700",
                    };
                    return (
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold ${config.color}`}
                      >
                        {config.label}
                      </span>
                    );
                  },
                },
                {
                  key: "status",
                  label: "Status",
                  render: (item) => {
                    const config = statusConfig[item.status] || {
                      label: item.status,
                      variant: "outline" as const,
                    };
                    return (
                      <Badge
                        variant={config.variant}
                        className="whitespace-nowrap"
                      >
                        {config.label}
                      </Badge>
                    );
                  },
                },
                {
                  key: "createdAt",
                  label: "Data",
                  render: (item) => (
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Calendar className="h-3.5 w-3.5" />
                      {formatDate(item.createdAt)}
                    </div>
                  ),
                },
                {
                  key: "sla",
                  label: "SLA",
                  render: (item) => {
                    const sla = calculateSLA(
                      item.slaDeadline,
                      item.createdAt,
                      item.status,
                    );
                    return (
                      <Badge
                        className={`font-semibold text-xs px-2 py-1 border whitespace-nowrap ${sla.color}`}
                      >
                        {sla.display}
                      </Badge>
                    );
                  },
                },
                {
                  key: "actions",
                  label: "Ações",
                  render: (item) => (
                    <div className="flex items-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() =>
                          navigate(`/occurrences/${item.protocol}`)
                        }
                        title="Visualizar"
                      >
                        <Eye className="h-5 w-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-7 w-7 transition-colors ${
                          item.status === "AWAITING_ANALYSIS"
                            ? "cursor-pointer hover:bg-green-50 text-green-600"
                            : "cursor-not-allowed text-gray-400"
                        }`}
                        onClick={() => {
                          if (item.status === "AWAITING_ANALYSIS") {
                            navigate(`/occurrences/${item.protocol}/screening`);
                          }
                        }}
                        disabled={item.status !== "AWAITING_ANALYSIS"}
                        title={
                          item.status === "AWAITING_ANALYSIS"
                            ? "Triagem"
                            : "Triagem já realizada"
                        }
                      >
                        <Play className="h-4 w-4" />
                      </Button>
                      {item.workOrderId ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 cursor-pointer hover:bg-blue-50 text-blue-600 transition-colors"
                          onClick={() =>
                            navigate(`/service-orders/view/${item.workOrderId}`)
                          }
                          title="Ver Ordem de Serviço vinculada"
                        >
                          <ClipboardList className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          className={`h-7 w-7 transition-colors ${
                            item.status === "IN_PROGRESS"
                              ? "cursor-pointer"
                              : "cursor-not-allowed text-gray-400"
                          }`}
                          onClick={() => {
                            if (item.status === "IN_PROGRESS") {
                              navigate("/service-orders/create", {
                                state: {
                                  fromOccurrence: true,
                                  occurrence: item,
                                },
                              });
                            }
                          }}
                          disabled={item.status !== "IN_PROGRESS"}
                          title={
                            item.status === "IN_PROGRESS"
                              ? "Criar Ordem de Serviço"
                              : "Disponível apenas para ocorrências em andamento"
                          }
                        >
                          <Newspaper className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ),
                },
              ]}
              data={data?.data || []}
              pagination={data?.meta}
              onPageChange={handlePageChange}
            />
          )
        }
      />
    </>
  );
};
